/**
 * Centralised branding context.
 *
 * - Fetches `GET /api/branding` once on app boot.
 * - Exposes `useBranding()` → `{ logo, logoLight, favicon, pwa192, pwa512,
 *     splashMedia, splashMediaType, splashMediaMime, version, refresh, isLoading }`.
 * - Falls back to bundled `/crickhere-icon.png` so UI never shows broken images.
 * - Caches the resolved branding in localStorage so the splash screen / first
 *   paint can show the latest known logo BEFORE the `/api/branding` request
 *   completes (eliminates the "old logo flash" on PWA cold-start).
 * - Updates the document's <link rel="icon"> + apple-touch-icon whenever
 *   `favicon` changes, so the browser tab icon flips instantly after upload.
 * - Cache-busts CDN URLs by appending `?v={version}` so admins see new logos
 *   immediately without a hard refresh.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const DEFAULT_DARK_LOGO = "/crickhere-mobile-login-logo.png";
const DEFAULT_LIGHT_LOGO = "/crickhere-mobile-login-logo.png";
const DEFAULT_FAVICON = "/favicon.ico";
const DEFAULT_PWA_192 = "/icons/icon-192x192.png";
const DEFAULT_PWA_512 = "/icons/icon-512x512.png";
const DEFAULT_SPLASH_MEDIA = "/logo-intro.mp4";
const DEFAULT_SPLASH_MEDIA_TYPE = "video";
const DEFAULT_SPLASH_MEDIA_MIME = "video/mp4";
const DEFAULT_SPLASH_DURATION_MS = 8000;
const CACHE_KEY = "crickhere_branding_cache_v2";

const BrandingContext = createContext({
  logo: DEFAULT_DARK_LOGO,
  logoLight: DEFAULT_LIGHT_LOGO,
  hasCustomLightLogo: false,
  favicon: DEFAULT_FAVICON,
  pwa192: DEFAULT_PWA_192,
  pwa512: DEFAULT_PWA_512,
  splashMedia: DEFAULT_SPLASH_MEDIA,
  splashMediaType: DEFAULT_SPLASH_MEDIA_TYPE,  // 'video' | 'gif' | 'image' | ''
  splashMediaMime: DEFAULT_SPLASH_MEDIA_MIME,
  splashDurationMs: DEFAULT_SPLASH_DURATION_MS,
  version: 0,
  isLoading: true,
  refresh: () => {},
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bust = (url, v) => {
  if (!url) return url;
  // Don't bust local assets — they're already cache-controlled by webpack
  if (url.startsWith("/")) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${v}`;
};

const updateFaviconTag = (faviconUrl) => {
  if (typeof document === "undefined" || !faviconUrl) return;
  // Standard favicon
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
  link.type = faviconUrl.endsWith(".svg") ? "image/svg+xml" : "image/png";
  // Apple touch icon
  let apple = document.querySelector("link[rel='apple-touch-icon']");
  if (!apple) {
    apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    document.head.appendChild(apple);
  }
  apple.href = faviconUrl;
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const writeCache = (state) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(state)); } catch {}
};

export const BrandingProvider = ({ children }) => {
  // Hydrate from localStorage cache so the splash screen / first paint shows
  // the latest known logo immediately (no flash of the default icon).
  const cached = readCache();
  // Apply cached favicon to the document tag synchronously so the browser tab
  // shows the latest icon before the /api/branding request resolves.
  if (cached?.favicon) updateFaviconTag(cached.favicon);

  const [state, setState] = useState({
    logo: cached?.logo || DEFAULT_DARK_LOGO,
    logoLight: cached?.logoLight || DEFAULT_LIGHT_LOGO,
    hasCustomLightLogo: Boolean(cached?.hasCustomLightLogo),
    favicon: cached?.favicon || DEFAULT_FAVICON,
    pwa192: cached?.pwa192 || DEFAULT_PWA_192,
    pwa512: cached?.pwa512 || DEFAULT_PWA_512,
    splashMedia: cached?.splashMedia || DEFAULT_SPLASH_MEDIA,
    splashMediaType: cached?.splashMediaType || DEFAULT_SPLASH_MEDIA_TYPE,
    splashMediaMime: cached?.splashMediaMime || DEFAULT_SPLASH_MEDIA_MIME,
    splashDurationMs: cached?.splashDurationMs || DEFAULT_SPLASH_DURATION_MS,
    version: cached?.version || 0,
    isLoading: !cached,
  });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branding`);
      const d = res.data || {};
      const v = d.version || 0;
      const next = {
        logo: bust(d.logo_dark, v) || DEFAULT_DARK_LOGO,
        logoLight: bust(d.logo_light, v) || DEFAULT_LIGHT_LOGO,
        hasCustomLightLogo: Boolean(d.logo_light),
        favicon: bust(d.favicon, v) || DEFAULT_FAVICON,
        pwa192: bust(d.pwa_192, v) || DEFAULT_PWA_192,
        pwa512: bust(d.pwa_512, v) || DEFAULT_PWA_512,
        splashMedia: bust(d.splash_media, v) || DEFAULT_SPLASH_MEDIA,
        splashMediaType: d.splash_media_type || DEFAULT_SPLASH_MEDIA_TYPE,
        splashMediaMime: d.splash_media_mime || DEFAULT_SPLASH_MEDIA_MIME,
        splashDurationMs: Number(d.splash_duration_ms) || DEFAULT_SPLASH_DURATION_MS,
        version: v,
        isLoading: false,
      };
      setState(next);
      writeCache(next);
      updateFaviconTag(next.favicon);
    } catch (e) {
      // Fall through to defaults — already set in initial state
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <BrandingContext.Provider value={{ ...state, refresh: load }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);

export default BrandingProvider;


